/**
 * 我的履歷（本人·桌機）· func_code = ee_my_resume。
 * 2026-10-30：除帳號資料（走我的個人資料）與管理端備註外，個人資料全部由本人填寫修改；
 * 身分證可按「顯示完整」看自己的號碼（每次記歷史 + user_logs）。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress } from '@mui/material';

import FunctionPageHeader from '../components/FunctionPageHeader';
import ResumeTabs, { ResumeTabsHandlers } from '../components/hr/ResumeTabs';
import { useEmbed } from '../contexts/EmbedContext';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';
import {
  createMyDetail, deleteMyDetail, getMyHistory, getMyResume, revealMyId, updateMyDetail,
  updateMyResume,
} from '../services/eeHrResumeService';
import { HrProfile } from '../types/eeHrResume';

const FUNC_CODE = 'ee_my_resume';

const EeMyResumePage: React.FC = () => {
  const { t } = useTranslation();
  const { embedded } = useEmbed();   // 聊天 modal 內嵌時隱藏頁首（§11：改身分證 / 看完整畫面）
  const { hasPermission, loading: permLoading } = usePermission();
  const { showApiError } = useMessage();
  const [profile, setProfile] = useState<HrProfile | null>(null);

  const load = useCallback(async () => {
    try { setProfile(await getMyResume()); } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => {
    if (!permLoading && hasPermission(FUNC_CODE, 'read')) load();
  }, [permLoading, hasPermission, load]);

  if (permLoading || !profile) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }
  if (!hasPermission(FUNC_CODE, 'read')) {
    return <Box p={3}><Alert severity="warning">{t('common.noPermission', '無此功能存取權限')}</Alert></Box>;
  }

  const handlers: ResumeTabsHandlers = {
    saveProfile: (u) => updateMyResume(u),
    reveal: () => revealMyId().then((r) => ({ id_number: r.id_number })),
    loadHistory: (offset, limit) => getMyHistory({ offset, limit }),
    createDetail: (section, data) => createMyDetail(section, data),
    updateDetail: (section, id, data) => updateMyDetail(section, id, data),
    deleteDetail: (section, id) => deleteMyDetail(section, id),
  };

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded && <FunctionPageHeader funcCode={FUNC_CODE} />}
      <Box sx={{ mt: embedded ? 0 : 2 }}>
        <ResumeTabs profile={profile} mode="self" onReload={load} handlers={handlers} />
      </Box>
    </Box>
  );
};

export default EeMyResumePage;
